// src/lib/pharmacyFirst/pathways/aht/aht-wt-semaglutide-rescreen.pathway.ts
//
// AHT Weight Management — Semaglutide (Wegovy®) Full Re-screen Pathway
// Source: AHT_Semaglutide_Wegovy_Clinical_Pathway.docx v1.4
//
// Triggered when a continuation consultation identifies a change since the last
// consultation. The exclusion items the initial pathway confirms via its
// confirmation card are asked here one at a time as individual yes/no questions
// (positive phrasing — a "yes" means the risk is present). The downstream PGD
// checks, interaction checks, cautions, attestation, and outcomes are the
// shared single source of truth.
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.

import type { Pathway } from "../../types/pathway.types";
import {
  PGD_FRESH_CHECKS,
  Q6_CAUTIONS,
  Q7_ATTEST,
  OUT_NO_F2F,
  OUT_CANNOT_SUPPLY,
  OUT_SUPPLY,
  semaglutideScreeningDownstreamNodes,
  semaglutideScreeningOutcomes,
} from "./_shared/semaglutide-screening-downstream";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

const Q1_F2F        = "sem-rescr-q1-f2f";
const EX1_THYROID   = "sem-rescr-ex1-thyroid-men2";
const EX2_PANCR     = "sem-rescr-ex2-pancreatitis";
const EX3_DIABETES  = "sem-rescr-ex3-diabetes";
const EX4_RENAL_HEP = "sem-rescr-ex4-renal-hepatic";
const EX5_GI        = "sem-rescr-ex5-gi-gastroparesis";
const EX6_PREG      = "sem-rescr-ex6-pregnancy";
const EX7_RETINO    = "sem-rescr-ex7-retinopathy";
const DOSE          = "sem-rescr-dose-select";
const ATTEST        = "sem-rescr-dose-confirm";

// ─────────────────────────────────────────────────────────────────────────────
// Pathway
// ─────────────────────────────────────────────────────────────────────────────

export const ahtWtSemaglutideRescreenPathway: Pathway = {
  id: "aht-wt-semaglutide-rescreen",
  name: "Semaglutide (Wegovy®) — Full Re-screen",
  population: "Adults attending face-to-face for initial supply",
  pgdRefs: ["pgd-aht-wt-semaglutide"],
  demographicConfiguration: { bannerFields: [] },
  entryNode: Q1_F2F,

  nodes: [
    // ─────────────────────────────────────────────────────────────────────────
    // Step 1 — Face-to-face confirmation (same as initial)
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q1_F2F,
      type: "yes_no",
      text: "Is the patient attending face-to-face at AHT registered premises?",
      hint: "Initial supply requires face-to-face consultation. Subsequent supplies may be conducted remotely in line with GPhC guidance (February 2025).",
      answerPolarity: "yes-positive",
      yes: EX1_THYROID,
      no: { outcomeId: OUT_NO_F2F },
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 2 — Exclusion questions (one per confirmation-card item, in order).
    // Positive phrasing: "yes" = risk present → cannot supply.
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: EX1_THYROID,
      type: "yes_no",
      text: "Personal or family history of thyroid cancer or MEN2?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX2_PANCR,
    },
    {
      id: EX2_PANCR,
      type: "yes_no",
      text: "History of pancreatitis (acute or chronic)?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX3_DIABETES,
    },
    {
      id: EX3_DIABETES,
      type: "yes_no",
      text: "Type 1 or Type 3c diabetes mellitus?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX4_RENAL_HEP,
    },
    {
      id: EX4_RENAL_HEP,
      type: "yes_no",
      text: "Severe renal impairment (CrCl <15 mL/min) or severe hepatic impairment?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX5_GI,
    },
    {
      id: EX5_GI,
      type: "yes_no",
      text: "Severe or unstable gastrointestinal disease including gastroparesis?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX6_PREG,
    },
    {
      id: EX6_PREG,
      type: "yes_no",
      text: "Pregnant, breastfeeding, or planning pregnancy?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX7_RETINO,
    },
    {
      id: EX7_RETINO,
      type: "yes_no",
      text: "Diabetic retinopathy or macular oedema?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: PGD_FRESH_CHECKS,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Steps 2b–4 — shared screening downstream (PGD fresh checks, interaction
    // checks, cautions review). The shared fixed-dose attestation is dropped and
    // the cautions node is re-routed to the dose selector below, because an
    // established patient is re-escalated to their dose on full re-screen.
    // ─────────────────────────────────────────────────────────────────────────
    ...semaglutideScreeningDownstreamNodes
      .filter((node) => node.id !== Q7_ATTEST)
      .map((node) => (node.id === Q6_CAUTIONS ? { ...node, next: DOSE } : node)),

    // ─────────────────────────────────────────────────────────────────────────
    // Step 5 — Dose selection (pharmacist sets the re-escalated dose).
    // Options mirrored EXACTLY from sem-trans-dose-select. KEEP IN SYNC with
    // aht-wt-semaglutide-transfer.pathway.ts and the drug's source doses.
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: DOSE,
      type: "single_select",
      text: "What dose should be supplied?",
      options: [
        { id: "sem-0_25mg", label: "0.25 mg SC once weekly" },
        { id: "sem-0_5mg",  label: "0.5 mg SC once weekly" },
        { id: "sem-1_0mg",  label: "1.0 mg SC once weekly" },
        { id: "sem-1_7mg",  label: "1.7 mg SC once weekly" },
        { id: "sem-2_4mg",  label: "2.4 mg SC once weekly" },
      ],
      routing: {
        "sem-0_25mg": ATTEST,
        "sem-0_5mg":  ATTEST,
        "sem-1_0mg":  ATTEST,
        "sem-1_7mg":  ATTEST,
        "sem-2_4mg":  ATTEST,
      },
      storesTo: "selectedDoseStep",
      required: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 6 — Supply attestation (selected dose). Dose-agnostic wording; the
    // selected dose is captured in selectedDoseStep, mirroring the transfer
    // pathway (no {{selected_dose}} interpolation is wired in the engine).
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: ATTEST,
      type: "attestation",
      text: "Step 5 — Confirm supply of Wegovy® (semaglutide) at the selected dose",
      attestationStatement:
        "I confirm that I have:\n" +
        "• Supplied 1 × Wegovy® pre-filled pen at the selected dose (4 doses = 28-day supply)\n" +
        "• Checked pen integrity and cold chain before supply\n" +
        "• Confirmed storage: 2–8°C before first use; after first use ≤30°C or refrigerated up to 6 weeks\n" +
        "• Counselled the patient on subcutaneous injection technique and injection site rotation\n" +
        "• Counselled the patient on missed dose guidance\n" +
        "• Advised the patient to inform their GP of initiation\n" +
        "• Provided patient information leaflet\n" +
        "• Recorded batch number and expiry date",
      conclusions: {
        optionA: { label: "Confirm and supply", next: { outcomeId: OUT_SUPPLY } },
        optionB: { label: "Do not supply",      next: { outcomeId: OUT_CANNOT_SUPPLY } },
      },
    },
  ],

  outcomes: {
    ...semaglutideScreeningOutcomes,
    [OUT_SUPPLY]: {
      ...semaglutideScreeningOutcomes[OUT_SUPPLY]!,
      title: "Supply complete — Wegovy® (semaglutide), selected dose",
    },
  },
};
